// #region Imports
import { useEffect, useState, useCallback } from 'react';
import funcionariosApi from '../services/funcionariosApi';
import { funcionarioFromBackend } from '../pages/Funcionarios/funcionario-utils';
// #endregion

export default function useFuncionarios() {
  // #region Estado base
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // #endregion

  // #region Efeito: carga inicial
  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const raw = await funcionariosApi.listAll();
        if (!active) return;
        setFuncionarios(raw.map(funcionarioFromBackend));
      } catch (e) {
        if (active) setError(e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);
  // #endregion

  // #region Listagem (filtros + paginação em memória)
  const listar = useCallback(({ q = '', page = 1, pageSize = 10, status = 'TODOS', cargo = 'TODOS' } = {}) => {
    const normalized = q.trim().toLowerCase();
    let filtered = funcionarios;
    if (normalized) {
      filtered = filtered.filter(f =>
        (f.nome || '').toLowerCase().includes(normalized) ||
        (f.cpf || '').toLowerCase().includes(normalized) ||
        (f.telefone || '').toLowerCase().includes(normalized) ||
        (f.cargo || '').toLowerCase().includes(normalized),
      );
    }
    if (status !== 'TODOS') {
      filtered = filtered.filter(f => (status === 'ATIVOS' ? f.ativo : !f.ativo));
    }
    if (cargo !== 'TODOS') {
      filtered = filtered.filter(f => f.cargo === cargo);
    }
    const total = filtered.length;
    const start = (page - 1) * pageSize;
    const data = filtered.slice(start, start + pageSize);
    return { data, total, page, pageSize };
  }, [funcionarios]);
  // #endregion

  // #region CRUD helpers
  const obter = useCallback(id => funcionarios.find(f => f.id === id) || null, [funcionarios]);

  const criar = useCallback(async payload => {
    const dto = await funcionariosApi.create(payload);
    const inst = funcionarioFromBackend(dto);
    setFuncionarios(prev => [inst, ...prev]);
    return inst;
  }, []);

  const atualizar = useCallback(async (id, payload) => {
    const dto = await funcionariosApi.update(id, payload);
    if (!dto) return null;
    const inst = funcionarioFromBackend(dto);
    setFuncionarios(prev => prev.map(f => (f.id === id ? inst : f)));
    return inst;
  }, []);

  const remover = useCallback(async id => {
    await funcionariosApi.remove(id);
    setFuncionarios(prev => prev.filter(f => f.id !== id));
  }, []);
  // #endregion

  // #region Export
  return { funcionarios, loading, error, listar, obter, criar, atualizar, remover };
  // #endregion
}
